import React, { useMemo, useState } from 'react'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    CircularProgress,
    ButtonBase,
    LinearProgress,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Theme,
    Tooltip,
    Typography,
    InputAdornment,
} from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import VisibilityIcon from '@material-ui/icons/Visibility'
import { DocumentPreviewModal } from '../../../ContractManager/DocumentPreviewModal'
import SearchIcon from '@material-ui/icons/Search'
import WarningIcon from '@material-ui/icons/Warning'
import AssignmentIcon from '@material-ui/icons/Assignment'
import { OrganizationContractTypeCoverage as CoverageGroup } from '../../../../models/Company/types'
import { formatDate } from '../../../ContractManager/utils'
import { CoverageWorkspaceFilters } from './CoverageWorkspaceFilters'
import {
    CoverageGrouping,
    CoverageGroupingToggle,
    CoveragePropertyView,
} from './CoveragePropertyView'
import {
    applyCoverageFilters,
    CoverageCompletionFilter,
    CoveragePropertyStatusFilter,
    CoverageScopeFilter,
    pivotCoverageByProperty,
} from './coverageFilters'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(4),
    },
    emptyState: {
        padding: theme.spacing(6, 3),
        textAlign: 'center',
        color: theme.palette.text.secondary,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(1),
        borderRadius: theme.spacing(1.5),
    },
    emptyIcon: {
        fontSize: 40,
        color: theme.palette.text.disabled,
        marginBottom: theme.spacing(0.5),
    },
    overview: {
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'stretch',
        gap: theme.spacing(1.5),
        width: '100%',
        minWidth: 0,
    },
    statCard: {
        flex: '1 1 0',
        minWidth: 0,
        padding: theme.spacing(1.75, 2),
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden',
    },
    attentionBanner: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.25, 2),
        borderRadius: theme.spacing(1.5),
        border: `1px solid #fecaca`,
        backgroundColor: '#fef2f2',
    },
    statValue: {
        fontSize: 24,
        fontWeight: 700,
        lineHeight: 1.1,
    },
    statLabel: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        marginTop: theme.spacing(0.5),
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    statSuccess: {
        color: theme.palette.success.main,
    },
    statWarning: {
        color: theme.palette.warning.main,
    },
    statDanger: {
        color: theme.palette.error.main,
    },
    accordion: {
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[1],
        borderRadius: theme.spacing(1.5),
        overflow: 'hidden',
        '&:before': {
            display: 'none',
        },
        '& + &': {
            marginTop: theme.spacing(1),
        },
    },
    accordionAttention: {
        borderLeft: `4px solid ${theme.palette.error.main}`,
    },
    accordionComplete: {
        borderLeft: `4px solid ${theme.palette.success.main}`,
    },
    summaryContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        width: '100%',
        paddingRight: theme.spacing(1),
    },
    typeTitle: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
        minWidth: 0,
    },
    typeTitleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0,
    },
    typeName: {
        fontWeight: 600,
    },
    typeDescription: {
        maxWidth: 420,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    stats: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexShrink: 0,
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
    },
    progressSummary: {
        minWidth: 120,
    },
    progressBar: {
        height: 8,
        borderRadius: 999,
        backgroundColor: theme.palette.grey[200],
        '& .MuiLinearProgress-bar': {
            borderRadius: 999,
        },
    },
    progressText: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(0.5),
    },
    details: {
        display: 'block',
        padding: theme.spacing(0, 2, 2),
    },
    sectionToolbar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(1.5),
        flexWrap: 'wrap',
    },
    propertySearch: {
        minWidth: 260,
        '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.background.paper,
        },
    },
    tableContainer: {
        maxHeight: 360,
    },
    coverageTable: {
        tableLayout: 'fixed',
        width: '100%',
    },
    equalColumnCell: {
        width: '33.333%',
    },
    headerCell: {
        fontWeight: 600,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.grey[100],
        fontSize: 13,
    },
    propertyCell: {
        fontWeight: 500,
    },
    missingRow: {
        backgroundColor: '#fff7ed',
    },
    statusCell: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    okIcon: {
        color: theme.palette.success.main,
        fontSize: 18,
    },
    missingIcon: {
        color: theme.palette.warning.main,
        fontSize: 18,
    },
    requiredMissing: {
        color: theme.palette.error.main,
        fontWeight: 600,
    },
    documentLinks: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
    },
    documentLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        maxWidth: 280,
        padding: theme.spacing(0.5, 1),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 999,
        backgroundColor: theme.palette.background.paper,
        textDecoration: 'none',
        cursor: 'pointer',
    },
    documentName: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
}))

interface OrganizationContractTypeCoverageProps {
    coverage: CoverageGroup[]
    isLoading: boolean
    selectedPropertyIds: number[] | null
    requiredOnly?: boolean
    itemNoun?: string
    selectedTagId?: number | null
    onSelectedTagIdChange?: (value: number | null) => void
}

export const OrganizationContractTypeCoverage: React.FC<OrganizationContractTypeCoverageProps> = ({
    coverage,
    isLoading,
    selectedPropertyIds,
    requiredOnly = true,
    itemNoun = 'contract',
    selectedTagId = null,
    onSelectedTagIdChange,
}) => {
    const classes = useStyles()
    const [propertySearchByType, setPropertySearchByType] = useState<
        Record<number, string>
    >({})
    const [typeSearch, setTypeSearch] = useState('')
    const [scope, setScope] = useState<CoverageScopeFilter>(
        requiredOnly ? 'required' : 'all',
    )
    const [completion, setCompletion] = useState<CoverageCompletionFilter>(
        'all',
    )
    const [
        propertyStatus,
        setPropertyStatus,
    ] = useState<CoveragePropertyStatusFilter>('all')
    const [coverageViewMode, setCoverageViewMode] = useState<CoverageGrouping>(
        'byType',
    )
    const [previewDoc, setPreviewDoc] = useState<{
        title: string
        fileUrl: string
    } | null>(null)

    const visibleCoverage = useMemo(
        () =>
            applyCoverageFilters(
                coverage.map((group) => ({
                    ...group,
                    typeId: group.contract_type.id,
                    typeName: group.contract_type.name,
                    typeDescription: group.contract_type.description,
                })),
                {
                    typeSearch,
                    scope,
                    completion,
                    propertyStatus,
                    selectedPropertyIds,
                },
            ),
        [
            coverage,
            typeSearch,
            scope,
            completion,
            propertyStatus,
            selectedPropertyIds,
        ],
    )

    const overview = useMemo(() => {
        const totals = visibleCoverage.reduce(
            (acc, group) => {
                acc.typeCount += 1
                acc.propertyChecks += group.properties.length
                acc.uploaded += group.uploaded_count
                acc.missing += group.missing_count
                acc.requiredMissing += group.required_missing_count
                return acc
            },
            {
                typeCount: 0,
                propertyChecks: 0,
                uploaded: 0,
                missing: 0,
                requiredMissing: 0,
            },
        )
        const completion =
            totals.propertyChecks === 0
                ? 0
                : Math.round((totals.uploaded / totals.propertyChecks) * 100)
        return { ...totals, completion }
    }, [visibleCoverage])

    const pivotedProperties = useMemo(
        () => pivotCoverageByProperty(visibleCoverage),
        [visibleCoverage],
    )

    if (isLoading) {
        return (
            <Paper variant="outlined" className={classes.loadingContainer}>
                <CircularProgress />
            </Paper>
        )
    }

    const noCatalogTypes = coverage.length === 0
    const typeLabel =
        itemNoun === 'certificate' ? 'certificate types' : 'contract types'

    return (
        <Box className={classes.root}>
            <CoverageWorkspaceFilters
                typeSearch={typeSearch}
                onTypeSearchChange={setTypeSearch}
                scope={scope}
                onScopeChange={setScope}
                completion={completion}
                onCompletionChange={setCompletion}
                propertyStatus={propertyStatus}
                onPropertyStatusChange={setPropertyStatus}
                itemNoun={itemNoun}
                selectedTagId={selectedTagId}
                onSelectedTagIdChange={onSelectedTagIdChange}
                toolbarExtra={
                    <CoverageGroupingToggle
                        value={coverageViewMode}
                        onChange={setCoverageViewMode}
                    />
                }
            />
            {overview.requiredMissing > 0 && (
                <Box className={classes.attentionBanner}>
                    <WarningIcon color="error" fontSize="small" />
                    <Typography variant="body2">
                        {overview.requiredMissing} required {itemNoun}
                        {overview.requiredMissing === 1 ? '' : 's'} still
                        missing across the current property set.
                    </Typography>
                </Box>
            )}
            {visibleCoverage.length === 0 ? (
                <Paper variant="outlined" className={classes.emptyState}>
                    <AssignmentIcon className={classes.emptyIcon} />
                    <Typography variant="subtitle1">
                        {noCatalogTypes
                            ? `No ${typeLabel} yet`
                            : `No ${itemNoun} coverage matches these filters`}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {noCatalogTypes
                            ? `Add ${typeLabel} and mark the properties that require them.`
                            : 'Try All types, clear the search, or widen the coverage filters.'}
                    </Typography>
                </Paper>
            ) : (
                <>
                    <Box className={classes.overview}>
                        <Paper className={classes.statCard} elevation={0}>
                            <Typography className={classes.statValue}>
                                {coverageViewMode === 'byProperty'
                                    ? pivotedProperties.length
                                    : overview.typeCount}
                            </Typography>
                            <Typography className={classes.statLabel}>
                                {coverageViewMode === 'byProperty'
                                    ? 'Properties'
                                    : itemNoun === 'certificate'
                                    ? 'Certificate types'
                                    : 'Contract types'}
                            </Typography>
                        </Paper>
                        <Paper className={classes.statCard} elevation={0}>
                            <Typography
                                className={`${classes.statValue} ${classes.statSuccess}`}
                            >
                                {overview.uploaded}
                            </Typography>
                            <Typography className={classes.statLabel}>
                                Uploaded
                            </Typography>
                        </Paper>
                        <Paper className={classes.statCard} elevation={0}>
                            <Typography
                                className={`${classes.statValue} ${classes.statWarning}`}
                            >
                                {overview.missing}
                            </Typography>
                            <Typography className={classes.statLabel}>
                                Missing
                            </Typography>
                        </Paper>
                        <Paper className={classes.statCard} elevation={0}>
                            <Typography
                                className={`${classes.statValue} ${
                                    overview.requiredMissing > 0
                                        ? classes.statDanger
                                        : classes.statSuccess
                                }`}
                            >
                                {overview.completion}%
                            </Typography>
                            <Typography className={classes.statLabel}>
                                Complete
                            </Typography>
                        </Paper>
                    </Box>
                    {coverageViewMode === 'byProperty' ? (
                        <CoveragePropertyView
                            propertyGroups={pivotedProperties}
                            itemNoun={itemNoun}
                        />
                    ) : (
                        visibleCoverage.map((group) => {
                            const propertyCount = group.properties.length
                            const propertySearch =
                                propertySearchByType[group.contract_type.id] ??
                                ''
                            const filteredProperties = propertySearch.trim()
                                ? group.properties.filter((property) =>
                                      property.name
                                          .toLowerCase()
                                          .includes(
                                              propertySearch
                                                  .trim()
                                                  .toLowerCase(),
                                          ),
                                  )
                                : group.properties
                            const percent =
                                propertyCount === 0
                                    ? 0
                                    : Math.round(
                                          (group.uploaded_count /
                                              propertyCount) *
                                              100,
                                      )
                            return (
                                <Accordion
                                    key={group.contract_type.id}
                                    className={`${classes.accordion} ${
                                        group.required_missing_count > 0
                                            ? classes.accordionAttention
                                            : percent === 100
                                            ? classes.accordionComplete
                                            : ''
                                    }`}
                                    defaultExpanded={group.required_count > 0}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                    >
                                        <Box className={classes.summaryContent}>
                                            <Box className={classes.typeTitle}>
                                                <Box
                                                    className={
                                                        classes.typeTitleRow
                                                    }
                                                >
                                                    <Typography
                                                        variant="subtitle1"
                                                        className={
                                                            classes.typeName
                                                        }
                                                        noWrap
                                                    >
                                                        {
                                                            group.contract_type
                                                                .name
                                                        }
                                                    </Typography>
                                                    {group.required_count >
                                                        0 && (
                                                        <Chip
                                                            size="small"
                                                            color="primary"
                                                            label={`Required for ${group.required_count}`}
                                                        />
                                                    )}
                                                    {percent === 100 && (
                                                        <Chip
                                                            size="small"
                                                            label="Complete"
                                                        />
                                                    )}
                                                </Box>
                                                {group.contract_type
                                                    .description && (
                                                    <Tooltip
                                                        title={
                                                            group.contract_type
                                                                .description
                                                        }
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                            className={
                                                                classes.typeDescription
                                                            }
                                                        >
                                                            {
                                                                group
                                                                    .contract_type
                                                                    .description
                                                            }
                                                        </Typography>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                            <Box className={classes.stats}>
                                                <Box
                                                    className={
                                                        classes.progressSummary
                                                    }
                                                >
                                                    <Box
                                                        className={
                                                            classes.progressText
                                                        }
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                        >
                                                            Coverage
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                        >
                                                            {percent}%
                                                        </Typography>
                                                    </Box>
                                                    <LinearProgress
                                                        className={
                                                            classes.progressBar
                                                        }
                                                        variant="determinate"
                                                        value={percent}
                                                        color={
                                                            group.required_missing_count >
                                                            0
                                                                ? 'secondary'
                                                                : 'primary'
                                                        }
                                                    />
                                                </Box>
                                                <Chip
                                                    size="small"
                                                    label={`${group.uploaded_count} uploaded`}
                                                />
                                                <Chip
                                                    size="small"
                                                    color={
                                                        group.required_missing_count >
                                                        0
                                                            ? 'secondary'
                                                            : 'default'
                                                    }
                                                    label={
                                                        scope === 'required'
                                                            ? `${group.required_missing_count} required missing`
                                                            : `${group.missing_count} missing`
                                                    }
                                                />
                                            </Box>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails
                                        className={classes.details}
                                    >
                                        <Box className={classes.sectionToolbar}>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                Showing{' '}
                                                {filteredProperties.length} of{' '}
                                                {group.properties.length}{' '}
                                                properties
                                            </Typography>
                                            <TextField
                                                className={
                                                    classes.propertySearch
                                                }
                                                placeholder="Search properties..."
                                                variant="outlined"
                                                size="small"
                                                value={propertySearch}
                                                onChange={(e) =>
                                                    setPropertySearchByType(
                                                        (prev) => ({
                                                            ...prev,
                                                            [group.contract_type
                                                                .id]:
                                                                e.target.value,
                                                        }),
                                                    )
                                                }
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon color="action" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Box>
                                        <TableContainer
                                            className={classes.tableContainer}
                                        >
                                            <Table
                                                size="small"
                                                stickyHeader
                                                className={
                                                    classes.coverageTable
                                                }
                                            >
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell
                                                            className={`${classes.headerCell} ${classes.equalColumnCell}`}
                                                        >
                                                            Property
                                                        </TableCell>
                                                        <TableCell
                                                            className={`${classes.headerCell} ${classes.equalColumnCell}`}
                                                        >
                                                            Status
                                                        </TableCell>
                                                        <TableCell
                                                            className={`${classes.headerCell} ${classes.equalColumnCell}`}
                                                        >
                                                            Contracts
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {filteredProperties.length ===
                                                    0 ? (
                                                        <TableRow>
                                                            <TableCell
                                                                colSpan={3}
                                                            >
                                                                <Box
                                                                    className={
                                                                        classes.emptyState
                                                                    }
                                                                >
                                                                    {propertySearch.trim()
                                                                        ? `No properties match "${propertySearch}".`
                                                                        : 'No properties match the current filter.'}
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        filteredProperties.map(
                                                            (property) => (
                                                                <TableRow
                                                                    key={
                                                                        property.id
                                                                    }
                                                                    className={
                                                                        !property.has_document &&
                                                                        property.is_required
                                                                            ? classes.missingRow
                                                                            : undefined
                                                                    }
                                                                >
                                                                    <TableCell
                                                                        className={`${classes.propertyCell} ${classes.equalColumnCell}`}
                                                                    >
                                                                        {
                                                                            property.name
                                                                        }
                                                                    </TableCell>
                                                                    <TableCell
                                                                        className={
                                                                            classes.equalColumnCell
                                                                        }
                                                                    >
                                                                        <Box
                                                                            className={
                                                                                classes.statusCell
                                                                            }
                                                                        >
                                                                            {property.has_document ? (
                                                                                <>
                                                                                    <CheckCircleIcon
                                                                                        className={
                                                                                            classes.okIcon
                                                                                        }
                                                                                    />
                                                                                    <Chip
                                                                                        size="small"
                                                                                        label={
                                                                                            property.is_required
                                                                                                ? 'Required uploaded'
                                                                                                : 'Uploaded'
                                                                                        }
                                                                                    />
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <WarningIcon
                                                                                        className={
                                                                                            classes.missingIcon
                                                                                        }
                                                                                    />
                                                                                    <Chip
                                                                                        size="small"
                                                                                        label="Missing"
                                                                                        className={
                                                                                            property.is_required
                                                                                                ? classes.requiredMissing
                                                                                                : undefined
                                                                                        }
                                                                                    />
                                                                                </>
                                                                            )}
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell
                                                                        className={
                                                                            classes.equalColumnCell
                                                                        }
                                                                    >
                                                                        {property
                                                                            .documents
                                                                            .length ===
                                                                        0 ? (
                                                                            '—'
                                                                        ) : (
                                                                            <Box
                                                                                className={
                                                                                    classes.documentLinks
                                                                                }
                                                                            >
                                                                                {property.documents.map(
                                                                                    (
                                                                                        doc,
                                                                                    ) => (
                                                                                        <Tooltip
                                                                                            key={
                                                                                                doc.id
                                                                                            }
                                                                                            title={`Uploaded ${formatDate(
                                                                                                doc.created_date,
                                                                                            )}`}
                                                                                        >
                                                                                            <ButtonBase
                                                                                                className={
                                                                                                    classes.documentLink
                                                                                                }
                                                                                                onClick={() =>
                                                                                                    setPreviewDoc(
                                                                                                        {
                                                                                                            title:
                                                                                                                doc.name,
                                                                                                            fileUrl:
                                                                                                                doc.document,
                                                                                                        },
                                                                                                    )
                                                                                                }
                                                                                            >
                                                                                                <Typography
                                                                                                    variant="body2"
                                                                                                    className={
                                                                                                        classes.documentName
                                                                                                    }
                                                                                                >
                                                                                                    {
                                                                                                        doc.name
                                                                                                    }
                                                                                                </Typography>
                                                                                                <VisibilityIcon fontSize="small" />
                                                                                            </ButtonBase>
                                                                                        </Tooltip>
                                                                                    ),
                                                                                )}
                                                                            </Box>
                                                                        )}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ),
                                                        )
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </AccordionDetails>
                                </Accordion>
                            )
                        })
                    )}
                </>
            )}
            <DocumentPreviewModal
                open={previewDoc != null}
                onClose={() => setPreviewDoc(null)}
                title={previewDoc?.title || 'Document'}
                fileUrl={previewDoc?.fileUrl || null}
            />
        </Box>
    )
}
